import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { addToCart } from "../../redux/spectsgenieSlice";
import api_services from "../../../src/services/api_services";
import auth_service from "../../../src/services/auth_service";
import toast from "react-hot-toast";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { useCart } from "../../context/CartContext";
import PrescriptionForm from "./PrescriptionForm";

const LensType = ({ isOpen, onClose, productInfo, onApiResponse }) => {
  const dispatch = useDispatch();
  const [selectedLens, setSelectedLens] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showLensPackages, setShowLensPackages] = useState(false);
  const [showPrescription, setShowPrescription] = useState(false);
  const { setCartCount } = useCart();
  const [lensOptions, setLensOptions] = useState([]);
  const [lensPackages, setLensPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [upgradeOptions, setUpgradeOptions] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedUpgrade, setSelectedUpgrade] = useState(null);
  const [previousSelectedPackageId, setPreviousSelectedPackageId] =
    useState(null);

  const lensId = productInfo?.id;
  const imageBaseURL = "https://newpos.spectsgenie.com/";

  let isLoggedIn = false;
  let customerId;
  try {
    const { id } = auth_service.getAccessTokenData();
    isLoggedIn = true;
    customerId = id;
  } catch (error) {
    isLoggedIn = false;
  }

  useEffect(() => {
    if (lensId) {
      const fetchLensTypes = async () => {
        try {
          const response = await api_services.fetchLensTypesId(lensId);
          setLensOptions(response.data || []);
        } catch (error) {
          console.error("Error fetching lens types:", error);
          setLensOptions([]);
        }
      };

      const fetchLensPackages = async () => {
        try {
          const response = await api_services.fetchLensPackagesId(
            lensId,
            productInfo.ca_id
          );
          setLensPackages(response.data || []);
        } catch (error) {
          console.error("Error fetching lens packages:", error);
          setLensPackages([]);
        }
      };

      fetchLensTypes();
      fetchLensPackages();
      setLoading(false);
    }
  }, [lensId]);

  const handleLensSelect = (lens) => {
    setSelectedLens(lens);
    setShowLensPackages(true);
  };

  useEffect(() => {
    if (selectedLens) {
      console.log("Updated Selected Lens Object:", selectedLens);
    }
  }, [selectedLens]);

  const handlePackageSelect = async (pkg) => {
    setSelectedPackage(pkg);

    // Check if this is a different package from the previously selected one
    if (pkg.id !== previousSelectedPackageId) {
      // Clear any previously selected upgrade when changing packages
      setSelectedUpgrade(null);
      setPreviousSelectedPackageId(pkg.id);
    }

    // If it's the same package as before, just show the upgrade modal with existing selection
    if (pkg.id === previousSelectedPackageId && upgradeOptions.length > 0) {
      setShowUpgradeModal(true);
      return;
    }

    // Otherwise, fetch upgrades for the newly selected package
    try {
      const response = await api_services.fetchLensPackageUpgradeById(pkg.id);
      console.log("Upgrade options response:", response);
      if (response && response.data && response.data.length > 0) {
        // Ensure all required fields are present in the data
        const enhancedOptions = response.data.map((option) => ({
          ...option,
          description: option.description || "",
          type: option.type || "",
        }));
        setUpgradeOptions(enhancedOptions);
        setShowUpgradeModal(true);
        console.log(
          "Setting showUpgradeModal to true with enhanced options:",
          enhancedOptions
        );
      } else {
        setUpgradeOptions([]);
        setShowUpgradeModal(false);
      }
    } catch (error) {
      console.error("Error fetching lens package upgrades:", error);
      setUpgradeOptions([]);
      setShowUpgradeModal(false);
    }
  };

  const handleUpgradeChange = (upgrade) => {
    setSelectedUpgrade(upgrade);
  };

  const handleConfirmUpgrade = () => {
    setShowUpgradeModal(false);
  };

  const handleClearUpgrade = () => {
    setSelectedUpgrade(null);
    setShowUpgradeModal(false);
  };

  const handleContinueToPrescription = () => {
    setShowPrescription(true);
  };

  const savePrescriptionToDB = async (prescriptionData) => {
    if (!isLoggedIn) {
      console.log("User not logged in, skipping prescription save to DB.");
      return;
    }

    const formData = new FormData();
    console.log("Prescription data to be saved:", prescriptionData);

    if (!prescriptionData) {
      console.error("Prescription data is null or undefined");
      return;
    }

    formData.append("customerId", customerId.toString());

    if (!prescriptionData.prescriptionFile) {
      formData.append(
        "rightSph",
        (prescriptionData.right?.sph || "0.00").toString()
      );
      formData.append(
        "rightCyl",
        (prescriptionData.right?.cyl || "0.00").toString()
      );
      formData.append(
        "rightAxis",
        (prescriptionData.right?.axis || "0").toString()
      );
      formData.append(
        "rightAdd",
        (prescriptionData.right?.add || "0.00").toString()
      );

      formData.append(
        "leftSph",
        (prescriptionData.left?.sph || "0.00").toString()
      );
      formData.append(
        "leftCyl",
        (prescriptionData.left?.cyl || "0.00").toString()
      );
      formData.append(
        "leftAxis",
        (prescriptionData.left?.axis || "0").toString()
      );
      formData.append(
        "leftAdd",
        (prescriptionData.left?.add || "0.00").toString()
      );

      formData.append("hasTwoPd", prescriptionData.hasTwoPD ? "1" : "0");

      if (prescriptionData.hasTwoPD) {
        formData.append("pdRight", (prescriptionData.rightPD || "").toString());
        formData.append("pdLeft", (prescriptionData.leftPD || "").toString());
        formData.append("pdSingle", "");
      } else {
        formData.append("pdSingle", (prescriptionData.pd || "").toString());
        formData.append("pdRight", "");
        formData.append("pdLeft", "");
      }
    } else {
      formData.append("rightSph", "0.00");
      formData.append("rightCyl", "0.00");
      formData.append("rightAxis", "0");
      formData.append("rightAdd", "0.00");
      formData.append("leftSph", "0.00");
      formData.append("leftCyl", "0.00");
      formData.append("leftAxis", "0");
      formData.append("leftAdd", "0.00");
      formData.append("pdSingle", "");
      formData.append("pdRight", "");
      formData.append("pdLeft", "");
      formData.append("hasTwoPd", "0");

      if (prescriptionData.prescriptionFile) {
        formData.append("prescriptionFile", prescriptionData.prescriptionFile);
      }
    }

    for (let pair of formData.entries()) {
      console.log(`${pair[0]}: ${pair[1]}`);
    }
  };

  const handleAddToCart = async (
    prescriptionData,
    confirmPrescription,
    uploadPrescription
  ) => {
    if (selectedLens && selectedPackage) {
      if (uploadPrescription === false && !confirmPrescription) {
        // toast.error("Please confirm your prescription details", {
        //   duration: 3000,
        //   position: "top-center",
        //   style: {
        //     background: "#333",
        //     color: "#fff",
        //   },
        // });
        return;
      }

      if (uploadPrescription === true && !prescriptionData.prescriptionFile) {
        // toast.error("Please upload your prescription", {
        //   duration: 3000,
        //   position: "top-center",
        //   style: {
        //     background: "#333",
        //     color: "#fff",
        //   },
        // });
        return;
      }

      // Initialize prescription data without base64 conversion
      let updatedPrescriptionData = { ...prescriptionData };

      const updatedPrice =
        Number(productInfo.price) +
        Number(selectedPackage.price) +
        (selectedUpgrade ? Number(selectedUpgrade.price) : 0);

      if (!isLoggedIn) {
        const localCart = JSON.parse(localStorage.getItem("cart")) || [];
        const productDetails = {
          product_id: productInfo.id,
          lens_package_id: selectedPackage.id,
          price: updatedPrice,
          lens_type_id: selectedLens.id,
          ca_id: productInfo.ca_id,
          lens: selectedLens,
          package: selectedPackage.name,
          prescription: updatedPrescriptionData,
          upgrade: selectedUpgrade ? selectedUpgrade : null,
        };

        const exists = localCart.some(
          (item) =>
            item.product_id === productDetails.product_id &&
            item.ca_id === productDetails.ca_id
        );

        if (!exists) {
          const updatedCart = [...localCart, productDetails];
          localStorage.setItem("cart", JSON.stringify(updatedCart));
          setCartCount(updatedCart.length);
        }

        // toast.success("Product added successfully", {
        //   duration: 3000,
        //   position: "top-center",
        //   style: {
        //     background: "#333",
        //     color: "#fff",
        //   },
        // });

        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        const payload = {
          product_id: productInfo.id,
          lens_package_id: selectedPackage.id,
          price: updatedPrice,
          customer_id: customerId,
          lens_type_id: selectedLens.id,
          ca_id: productInfo.ca_id,
          prescription: updatedPrescriptionData,
          upgrade_id: selectedUpgrade ? selectedUpgrade.id : null,
        };

        try {
          await savePrescriptionToDB(updatedPrescriptionData);
          
          // Add the new savePrescriptionFile API call when prescription file is uploaded
          if (uploadPrescription && prescriptionData.prescriptionFile) {
            try {
              const fileUploadResponse = await api_services.savePrescriptionFile({
                user_id: customerId,
                file: prescriptionData.prescriptionFile
              });
              
              if (fileUploadResponse.success) {
                console.log("Prescription file saved successfully", fileUploadResponse);
                // Create a new prescription data object without the prescriptionFile property
                // but with the prescriptionFilePath from the response
                const { prescriptionFile, ...prescriptionDataWithoutFile } = updatedPrescriptionData;
                updatedPrescriptionData = {
                  ...prescriptionDataWithoutFile,
                  prescriptionFilePath: fileUploadResponse.data.path
                };
                
                // Update the payload with the new prescription data that includes the file path but not the file
                payload.prescription = updatedPrescriptionData;
              } else {
                console.error("Error saving prescription file:", fileUploadResponse);
                // toast.error("Warning: Prescription file could not be saved", {
                //   duration: 3000,
                //   position: "top-center",
                //   style: {
                //     background: "#ff9800",
                //     color: "#fff",
                //   },
                // });
              }
            } catch (fileError) {
              console.error("Error saving prescription file:", fileError);
              // toast.error("Warning: Prescription file could not be saved", {
              //   duration: 3000,
              //   position: "top-center",
              //   style: {
              //     background: "#ff9800",
              //     color: "#fff",
              //   },
              // });
            }
          }
          
          // Now proceed with adding to cart using the updated prescription data that includes the file path
          const response = await api_services.addItemToCart(payload);
          if (response.success) {
            dispatch(
              addToCart({
                ...productInfo,
                lens: selectedLens,
                package: selectedPackage.name,
                price: updatedPrice,
                prescription: updatedPrescriptionData,
                upgrade: selectedUpgrade ? selectedUpgrade.name : null,
              })
            );
            const cartResponse = await api_services.fetchCartCountById(
              customerId
            );
            setCartCount(cartResponse.totalItems);
            onApiResponse(response);

            setTimeout(() => {
              onClose();
            }, 1000);
          } else {
            toast.error("Failed to add product to cart", {
              duration: 3000,
              position: "top-center",
              style: {
                background: "#333",
                color: "#fff",
              },
            });
            onApiResponse(response);
          }
        } catch (error) {
          console.error("Error adding to cart:", error);
          toast.error("Failed to add product to cart", {
            duration: 3000,
            position: "top-center",
            style: {
              background: "#333",
              color: "#fff",
            },
          });
          onApiResponse({ success: false, error });
        }
      }
    } else {
      toast.error("Please select lens type and package", {
        duration: 3000,
        position: "top-center",
        style: {
          background: "#333",
          color: "#fff",
        },
      });
    }
  };

  // Remove these helper functions
  // const convertFileToBase64 = (file) => { ... };
  // const compressImage = (file) => { ... };
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };
  
  // Add this helper function to compress images
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          // Calculate new dimensions (max 1200px width/height)
          let width = img.width;
          let height = img.height;
          if (width > height) {
            if (width > 1200) {
              height = Math.round((height * 1200) / width);
              width = 1200;
            }
          } else {
            if (height > 1200) {
              width = Math.round((width * 1200) / height);
              height = 1200;
            }
          }
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob with reduced quality
          canvas.toBlob(
            (blob) => {
              // Create a new file from the blob
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            },
            'image/jpeg',
            0.7  // Quality parameter (0.7 = 70% quality)
          );
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  };
  
  const totalPrice =
    Number(productInfo?.price || 0) +
    (selectedPackage ? Number(selectedPackage.price) : 0) +
    (selectedUpgrade ? Number(selectedUpgrade.price) : 0);

  // Emit event when modal is opened/closed to control bottom nav visibility
  useEffect(() => {
    const event = new CustomEvent('lensTypeModalState', { 
      detail: { isOpen } 
    });
    window.dispatchEvent(event);
    
    // Cleanup
    return () => {
      const cleanupEvent = new CustomEvent('lensTypeModalState', { 
        detail: { isOpen: false } 
      });
      window.dispatchEvent(cleanupEvent);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // const isSingleVision = selectedLens?.name?.toLowerCase().includes("single vision");

  return (
    <>
      <div className="fixed inset-0 flex justify-end bg-black bg-opacity-50 z-50">
        <div className="w-full max-w-[550px] bg-white shadow-lg h-full overflow-y-auto">
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
            <div className="p-3">
              <div className="flex items-center gap-2">
                {(showLensPackages || showPrescription) && (
                  <button
                    onClick={() => {
                      if (showPrescription) {
                        setShowPrescription(false);
                      } else {
                        setShowLensPackages(false);
                        setSelectedPackage(null);
                      }
                    }}
                    className="flex items-center gap-1 px-2 py-1 text-sm font-medium text-[#00007a] hover:text-[#1F509A] transition-colors rounded hover:bg-blue-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Back
                  </button>
                )}
                <h2 className="text-lg font-bold flex-grow text-[#00007a]">
                  {showPrescription
                    ? "Add Prescription"
                    : showLensPackages
                    ? "Select Lens Package"
                    : "Select Lens Type"}
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded hover:bg-gray-100"
                  aria-label="Close"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 p-3">
            {!showLensPackages &&
              !showPrescription &&
              lensOptions.map((option) => (
                <label
                  key={option.id}
                  className={`flex gap-3 items-center p-3 rounded shadow cursor-pointer border relative ${
                    selectedLens?.id === option.id
                      ? "border-[#00007a] bg-blue-50"
                      : "border-gray-200 hover:border-blue-300"
                  }`}
                  onClick={() => handleLensSelect(option)}
                >
                  <div className="flex-shrink-0 w-16 h-16 bg-gray-50 rounded p-1 flex items-center justify-center border border-gray-200">
                    <img
                      src={`${imageBaseURL}${option.icon}`}
                      alt={option.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-base font-bold text-[#00007a]">
                      {option.name}
                    </h3>
                    <p className="text-xs text-gray-600">
                      {option.description}
                    </p>
                  </div>
                  {selectedLens?.id === option.id && (
                    <div className="absolute top-2 right-2 text-[#00007a] bg-blue-100 rounded-full p-0.5">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        className="w-4 h-4"
                        viewBox="0 0 16 16"
                      >
                        <path d="M13.485 1.929a1.5 1.5 0 0 1 0 2.122l-7.778 7.778-3.536-3.536a1.5 1.5 0 0 1 2.122-2.122l2.121 2.12 6.364-6.363a1.5 1.5 0 0 1 2.122 0z" />
                      </svg>
                    </div>
                  )}
                </label>
              ))}

            {showLensPackages &&
              !showPrescription &&
              lensPackages
                .filter((pkg) => pkg.lens_type_ids === selectedLens?.uid)
                .map((pkg) => (
                  <div
                    key={pkg.id}
                    className={`p-3 rounded shadow cursor-pointer border relative ${
                      selectedPackage?.id === pkg.id
                        ? "border-[#00007a] bg-blue-50"
                        : "border-gray-200 hover:border-blue-300"
                    }`}
                    onClick={() => handlePackageSelect(pkg)}
                  >
                    <div className="flex-grow">
                      <h3 className="text-base font-bold text-[#00007a] mb-1">
                        {pkg.name}
                      </h3>
                      <div>
                        <p className="text-xs text-gray-600 whitespace-pre-line">
                          {pkg.description}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-amber-600 text-white">
                            Material: {pkg.lens_material}
                          </span>
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-[#00007a] text-white">
                            Price: ₹{pkg.price === "0" ? "Free" : pkg.price}
                          </span>
                        </div>
                      </div>

                      {selectedPackage?.id === pkg.id && selectedUpgrade && (
                        <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                          <p className="text-xs font-medium text-[#00007a] flex items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3 mr-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Selected Upgrade: {selectedUpgrade.name} (+₹
                            {selectedUpgrade.price})
                          </p>
                        </div>
                      )}
                      
                      {selectedPackage?.id === pkg.id && (
                        <div className="absolute top-2 right-2 text-[#00007a] bg-blue-100 rounded-full p-0.5">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            className="w-4 h-4"
                            viewBox="0 0 16 16"
                          >
                            <path d="M13.485 1.929a1.5 1.5 0 0 1 0 2.122l-7.778 7.778-3.536-3.536a1.5 1.5 0 0 1 2.122-2.122l2.121 2.12 6.364-6.363a1.5 1.5 0 0 1 2.122 0z" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

            {showPrescription && (
              <PrescriptionForm
                // isSingleVision={isSingleVision}
                handleAddToCart={handleAddToCart}
                totalPrice={totalPrice}
              />
            )}
          </div>

          {selectedPackage && !showPrescription && (
            <div className="sticky bottom-0 left-0 right-0 p-3 border-t bg-white shadow">
              <div className="bg-gray-50 p-3 rounded border border-gray-200">
                <h3 className="text-base font-bold text-[#00007a]">
                  Total Price: ₹{totalPrice}
                </h3>
                {selectedUpgrade && (
                  <p className="text-xs text-gray-700 mt-1">
                    Includes upgrade: {selectedUpgrade.name} (+₹
                    {selectedUpgrade.price})
                  </p>
                )}
                <button
                  onClick={handleContinueToPrescription}
                  className="w-full bg-[#00007a] text-white py-2 rounded mt-2 font-medium text-sm hover:bg-[#000055] transition-colors"
                >
                  Continue to Prescription
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && upgradeOptions.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-[60]">
          <div className="bg-white rounded shadow-lg w-[400px] max-w-full overflow-hidden">
            {/* Header with close button */}
            <div className="flex justify-between items-center px-3 py-2 border-b bg-[#00007a] text-white">
              <h2 className="text-base font-bold">Select a Lens Upgrade</h2>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-white hover:text-gray-200 transition-colors"
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            {/* Upgrade options */}
            <div className="py-2 max-h-80 overflow-y-auto">
              {upgradeOptions.map((option) => (
                <label
                  key={option.id}
                  className={`flex items-center px-3 py-3 cursor-pointer border-b border-gray-100 ${
                    selectedUpgrade && selectedUpgrade.id === option.id
                      ? "bg-blue-50"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-center mr-3">
                    <div
                      className={`w-4 h-4 rounded-full flex items-center justify-center ${
                        selectedUpgrade && selectedUpgrade.id === option.id
                          ? "border-2 border-[#00007a]"
                          : "border border-gray-300"
                      }`}
                    >
                      {selectedUpgrade && selectedUpgrade.id === option.id && (
                        <div className="w-2 h-2 rounded-full bg-[#00007a]"></div>
                      )}
                    </div>
                    <input
                      type="radio"
                      name="upgrade"
                      value={option.id}
                      checked={
                        selectedUpgrade && selectedUpgrade.id === option.id
                      }
                      onChange={() => handleUpgradeChange(option)}
                      className="opacity-0 absolute w-0 h-0"
                    />
                  </div>

                  <div className="flex-grow">
                    <div className="font-bold text-[#00007a] text-sm">
                      {option.name}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {option.type && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium text-white bg-amber-600">
                          Type: {option.type}
                        </span>
                      )}

                      <span className="px-2 py-0.5 rounded text-xs font-medium text-white bg-[#00007a]">
                        Price: +₹{option.price}
                      </span>
                    </div>

                    {option.description && (
                      <div className="text-gray-600 text-xs mt-1">
                        {option.description}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>

            {/* Action buttons */}
            <div className="px-3 py-3 bg-gray-50 flex gap-2 justify-between">
              <button
                className="flex-1 py-2 px-3 border border-gray-300 rounded text-gray-700 text-sm font-medium bg-white hover:bg-gray-50 transition-colors"
                onClick={
                  selectedUpgrade
                    ? handleClearUpgrade
                    : () => setShowUpgradeModal(false)
                }
              >
                {selectedUpgrade ? "Clear Selection" : "Skip Upgrade"}
              </button>
              <button
                className="flex-1 py-2 px-3 bg-[#00007a] text-white rounded text-sm font-medium hover:bg-[#000055] transition-colors"
                onClick={handleConfirmUpgrade}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LensType;
